import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {
  useGetConfiguration,
  useCheckLoading,
} from '@adloid-technologies/discover-sdk';
import { logEvent, initLog, registerDefaults, getOrCreateSessionId, getDeviceType, getUtmParams, getReferrer } from './logger';
import { AnalyticsIds } from './analytics';
import { getMode, getCountryParameter, getDealerNameParameter } from './common';
import { useTranslation } from './translations';
import {
  dispatchAutoIntelEvent,
  AutoIntelEvents,
  AutoIntelEventType,
  getAutoIntelRPOCode,
} from './autointel';
import { uimetadata } from './metadata';

const MODEL =
  uimetadata.config?.model || Object.keys(uimetadata.models || {})[0] || '';

// Same derivation as main.tsx: the variant the app loads first. The SDK's
// configuration (and so variantID) doesn't exist yet when the initial load
// starts, so this stands in as targetModel on that first modelLoadStart.
const DEFAULT_VARIANT: string = (() => {
  const variants = (uimetadata as any).models?.[MODEL]?.variants ?? {};
  return (
    (Object.entries(variants).find(([, v]: any) => v?.isDefault) as
      | [string, any]
      | undefined)?.[0] ??
    Object.keys(variants)[0] ??
    ''
  );
})();

export interface DefaultSegmentations {
  // Computed fresh every render, from app/route state.
  fromLink: string;
  linkId: string;
  modelName: string;
  variantName: string;
  projectName: string;
  product: string;
  currentURL: string;
  deployedMarket: string;
  activeModel: string;
  activeVariant: string;
  model1Name: string;
  variant1Name: string;
  model2Name: string;
  variant2Name: string;
  formFactor: string;
  vehicleType: string;
  orientation: string;
  integrationType: string;
  parent: string;
  // Sticky — persist across events until explicitly changed.
  sessionId: string;
  appEnv: string;
  language: string;
  theme: string;
  dealerName: string;
  modelYear: string;
  driveType: string;
  exteriorColor: string;
  wheels: string;
  interior: string;
  currentTab: string;
  viewMode: string;
  currentPrice: string;
  matchingVehicleCount: string;
  deviceType: string;
}

type StickySegmentations = Pick<
  DefaultSegmentations,
  | 'sessionId'
  | 'appEnv'
  | 'language'
  | 'theme'
  | 'dealerName'
  | 'modelYear'
  | 'driveType'
  | 'exteriorColor'
  | 'wheels'
  | 'interior'
  | 'currentTab'
  | 'viewMode'
  | 'currentPrice'
  | 'matchingVehicleCount'
  | 'deviceType'
>;

// sessionId/appEnv/deviceType are known synchronously, so they are seeded here
// rather than in an effect: events fired on first render (landing) would
// otherwise carry them blank.
const initialSticky = (): StickySegmentations => ({
  sessionId: getOrCreateSessionId(),
  appEnv: getMode(),
  language: '',
  theme: '',
  // A ?dealerName= link pre-selects the dealer, so it is known from the first event.
  dealerName: getDealerNameParameter(),
  modelYear: '',
  driveType: '',
  exteriorColor: '',
  wheels: '',
  interior: '',
  currentTab: '',
  viewMode: '',
  currentPrice: '',
  matchingVehicleCount: '',
  deviceType: getDeviceType(),
});

interface AnalyticsSegmentationContextValue {
  sticky: StickySegmentations;
  setSticky: (patch: Partial<StickySegmentations>) => void;
  /** Fires `mixpanelStart` for a module (landing/configurator) with UTM + referrer segmentation. */
  fireModuleStart: (moduleName: string) => void;
  visitedTabs: React.MutableRefObject<Set<string>>;
}

const AnalyticsSegmentationContext = createContext<
  AnalyticsSegmentationContextValue | undefined
>(undefined);

const SESSION_END_TRIGGER = {
  pagehide: 'pagehide',
  hidden: 'visibilitychange',
} as const;

export const AnalyticsSegmentationProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [sticky, setStickyState] = useState<StickySegmentations>(
    initialSticky
  );
  const visitedTabs = useRef<Set<string>>(new Set());
  const sessionStartTimeRef = useRef<number>(Date.now());
  const lastActiveTabRef = useRef<string>('');

  const setSticky = useCallback((patch: Partial<StickySegmentations>) => {
    setStickyState((prev) => ({ ...prev, ...patch }));
    if (patch.currentTab) {
      visitedTabs.current.add(patch.currentTab);
      lastActiveTabRef.current = patch.currentTab;
    }
  }, []);

  // Boot the SDK + seed the fields that only need to be computed once.
  useEffect(() => {
    const sessionId = getOrCreateSessionId();
    const appEnv = getMode();
    const deviceType = getDeviceType();
    setStickyState((prev) => ({ ...prev, sessionId, appEnv, deviceType }));
    initLog();
  }, []);

  const fireModuleStart = useCallback((moduleName: string) => {
    const utm = getUtmParams();
    logEvent('add_event', {
      key: AnalyticsIds.mixpanelStart,
      segmentation: {
        parent: moduleName,
        primaryTypeName: utm.utmSource,
        secondaryTypeName: utm.utmMedium,
        tertiaryTypeName: utm.utmCampaign,
        quaternaryTypeName: getReferrer(),
      },
    });
  }, []);

  // sessionEnd: fires when the page is hidden or unloaded. `pagehide` and
  // `visibilitychange → hidden` usually both fire on a real exit, so they are
  // de-duplicated; the guard re-arms when the page becomes visible again so a
  // tab switch mid-session cannot swallow the real end. Default segmentations
  // are attached via registerDefaults (super properties), not per event,
  // because per-event props are lost during teardown.
  useEffect(() => {
    let fired = false;
    const fireSessionEnd = (trigger: string) => {
      if (fired) return;
      fired = true;
      const durationSeconds = Math.round(
        (Date.now() - sessionStartTimeRef.current) / 1000
      );
      logEvent('add_event', {
        key: AnalyticsIds.sessionEnd,
        segmentation: {
          parent: trigger,
          primaryTypeName: String(durationSeconds),
          secondaryTypeName: Array.from(visitedTabs.current).join(','),
          tertiaryTypeName: lastActiveTabRef.current,
        },
      });
    };
    const onPageHide = () => fireSessionEnd(SESSION_END_TRIGGER.pagehide);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        fireSessionEnd(SESSION_END_TRIGGER.hidden);
      } else {
        fired = false;
      }
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const value = useMemo(
    () => ({ sticky, setSticky, fireModuleStart, visitedTabs }),
    [sticky, setSticky, fireModuleStart]
  );

  return (
    <AnalyticsSegmentationContext.Provider value={value}>
      {children}
    </AnalyticsSegmentationContext.Provider>
  );
};

export const useAnalyticsSegmentationContext =
  (): AnalyticsSegmentationContextValue => {
    const ctx = useContext(AnalyticsSegmentationContext);
    if (!ctx) {
      throw new Error(
        'useAnalyticsSegmentationContext must be used within an AnalyticsSegmentationProvider'
      );
    }
    return ctx;
  };

/** Hook to get default segmentations for analytics events. */
export const useDefaultSegmentations = (): DefaultSegmentations => {
  const { variantID, view } = useGetConfiguration();
  const { sticky } = useAnalyticsSegmentationContext();
  const { currentLanguage } = useTranslation();
  const country = getCountryParameter();
  const linkId = (import.meta.env.VITE_LINK_ID as string | undefined) || '2378';

  const getOrientation = (): string => {
    if (typeof window === 'undefined') return 'portrait';
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  };

  return useMemo(
    () => ({
      fromLink: getReferrer(),
      linkId,
      modelName: MODEL,
      variantName: variantID,
      projectName: 'metadome-autodome2-0-web',
      product: 'discover',
      currentURL: window.location.href,
      deployedMarket: country,
      activeModel: MODEL,
      activeVariant: variantID,
      model1Name: '',
      variant1Name: '',
      model2Name: '',
      variant2Name: '',
      formFactor: '3D',
      vehicleType: 'car',
      orientation: getOrientation(),
      integrationType: 'Main',
      parent: view.type,
      ...sticky,
      language: sticky.language || currentLanguage,
    }),
    [variantID, country, view.type, linkId, sticky, currentLanguage]
  );
};

/** Hook to track timed events (start/end pairs). */
export const useTimedEvent = () => {
  const timedEventsRef = useRef<Map<string, number>>(new Map());
  const defaultSegmentations = useDefaultSegmentations();

  const startTimedEvent = useCallback(
    (
      eventKey: string,
      additionalSegmentations: Record<string, any> = {},
      // performance.now() timestamp the work actually began (0 = page load),
      // for loads that start before analytics is initialised.
      startedAt: number = performance.now()
    ) => {
      timedEventsRef.current.set(eventKey, startedAt);
      logEvent('start_event', {
        key: eventKey,
        segmentation: {
          ...defaultSegmentations,
          ...additionalSegmentations,
        },
      });
    },
    [defaultSegmentations]
  );

  const endTimedEvent = useCallback(
    (eventKey: string, additionalSegmentations: Record<string, any> = {}) => {
      const startTime = timedEventsRef.current.get(eventKey);
      if (startTime !== undefined) {
        timedEventsRef.current.delete(eventKey);
        logEvent('end_event', {
          key: eventKey,
          segmentation: {
            ...defaultSegmentations,
            ...additionalSegmentations,
            // Explicit so the initial load (started before analytics init)
            // is timed correctly on every provider.
            duration_ms: Math.round(performance.now() - startTime),
          },
        });
      }
    },
    [defaultSegmentations]
  );

  return { startTimedEvent, endTimedEvent };
};

/** Hook to log standard events with default segmentations. */
export const useLogEvent = () => {
  const defaultSegmentations = useDefaultSegmentations();

  const trackEvent = useCallback(
    (eventKey: string, additionalSegmentations: Record<string, any> = {}) => {
      logEvent('add_event', {
        key: eventKey,
        segmentation: {
          ...defaultSegmentations,
          ...additionalSegmentations,
        },
      });
    },
    [defaultSegmentations]
  );

  return { trackEvent, defaultSegmentations };
};

// Shared so other failure paths (modelLoadError in App.tsx) carry the same
// load properties as the start/time/end events.
let currentLoadInfo: Record<string, string | number | boolean> = {};
let loadSequenceCounter = 0;

/** Named load properties for the load that is (or was last) in flight. */
export const getLoadInfo = (): Record<string, string | number | boolean> =>
  Object.keys(currentLoadInfo).length
    ? currentLoadInfo
    : {
        loadType: 'initial',
        isInitialLoad: true,
        loadSequence: Math.max(loadSequenceCounter, 1),
      };

/**
 * Fires modelLoadStart / modelLoadTime / modelLoadEnd for every variant load
 * (initial load and each later switch) with the sheet's named properties:
 * loadType (initial | subsequent), isInitialLoad, targetModel, previousModel,
 * loadSequence, triggeredBy. `parent` stays the active view (default
 * segmentation). `cached` is not sent: the app cannot tell.
 */
export const useLoadingAnalytics = () => {
  const { variant: variantLoading } = useCheckLoading();
  const { variantID } = useGetConfiguration();
  const { sticky } = useAnalyticsSegmentationContext();
  const { startTimedEvent, endTimedEvent } = useTimedEvent();
  const { trackEvent } = useLogEvent();

  const prevLoadingRef = useRef(false);
  const lastLoadedVariantRef = useRef('');
  const lastModelYearRef = useRef('');

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;

    if (!wasLoading && variantLoading) {
      loadSequenceCounter += 1;
      const isInitial = loadSequenceCounter === 1;
      let triggeredBy = '';
      if (!isInitial) {
        if (sticky.modelYear && sticky.modelYear !== lastModelYearRef.current) {
          triggeredBy = AnalyticsIds.modelYearInteract;
        } else {
          triggeredBy = AnalyticsIds.variantInteract;
        }
      }
      lastModelYearRef.current = sticky.modelYear;

      currentLoadInfo = {
        loadType: isInitial ? 'initial' : 'subsequent',
        isInitialLoad: isInitial,
        targetModel: variantID || DEFAULT_VARIANT,
        previousModel: lastLoadedVariantRef.current,
        loadSequence: loadSequenceCounter,
        triggeredBy,
      };
      trackEvent(AnalyticsIds.modelLoadStart, currentLoadInfo);
      // The initial load begins at page load, before analytics is initialised.
      startTimedEvent(
        AnalyticsIds.modelLoadTime,
        currentLoadInfo,
        isInitial ? 0 : undefined
      );
    } else if (wasLoading && !variantLoading) {
      lastLoadedVariantRef.current = variantID;
      // By now the real variant is known; it can differ from the default
      // used at start (e.g. the presentation journey loads another one).
      currentLoadInfo = {
        ...currentLoadInfo,
        targetModel: variantID || currentLoadInfo.targetModel,
      };
      trackEvent(AnalyticsIds.modelLoadEnd, currentLoadInfo);
      endTimedEvent(AnalyticsIds.modelLoadTime, currentLoadInfo);
    }

    prevLoadingRef.current = variantLoading;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantLoading]);
};

/**
 * Keeps the default segmentations registered as super properties so every
 * event carries the full block, including ones fired during page teardown.
 */
export const useRegisterDefaultSegmentations = () => {
  const defaults = useDefaultSegmentations();
  useEffect(() => {
    const props: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(defaults)) {
      props[k] = v ?? '';
    }
    registerDefaults(props);
  }, [defaults]);
};

/**
 * Hook to track events with both analytics and AutoIntel.
 * Use this when you want to fire both systems simultaneously.
 */
export const useAutoIntelTracking = () => {
  const { trackEvent, defaultSegmentations } = useLogEvent();
  const { variantID } = useGetConfiguration();

  const trackWithAutoIntel = useCallback(
    (
      analyticsEventKey: string,
      autoIntelEventType: AutoIntelEventType | string,
      itemId: string,
      additionalSegmentations: Record<string, any> = {},
      additionalSubAction?: string
    ) => {
      const rpoCode = getAutoIntelRPOCode(itemId) || itemId;

      trackEvent(analyticsEventKey, {
        ...additionalSegmentations,
        autoIntelItemId: itemId,
        autoIntelRPOCode: rpoCode,
      });

      dispatchAutoIntelEvent(autoIntelEventType, rpoCode, additionalSubAction, {
        productId: MODEL,
        id: variantID,
        features: rpoCode ? [rpoCode] : [],
      });
    },
    [trackEvent, variantID]
  );

  const trackAutoIntelOnly = useCallback(
    (
      autoIntelEventType: AutoIntelEventType | string,
      itemId: string,
      additionalContext?: string,
      additionalSubAction?: string
    ) => {
      const rpoCode = getAutoIntelRPOCode(itemId) || itemId;

      dispatchAutoIntelEvent(
        autoIntelEventType,
        rpoCode,
        additionalSubAction || additionalContext,
        {
          productId: MODEL,
          id: variantID,
          features: rpoCode ? [rpoCode] : [],
        }
      );
    },
    [variantID]
  );

  return {
    trackEvent,
    trackWithAutoIntel,
    trackAutoIntelOnly,
    defaultSegmentations,
  };
};

export { AnalyticsIds, AutoIntelEvents };
